/**
 * Centralized, typed access to the SPA's PUBLIC configuration.
 *
 * ── WHY THIS FILE CHANGED (release/platform closure) ─────────────────────────────────────────────
 * These values used to be read straight out of `import.meta.env` at BUILD time, which meant the
 * bundle's identity depended on which environment it had been built for: an INTEG build baked the
 * INTEG API URL and Entra client id in, and a PROD build would have baked different ones. Two
 * different files, two different hashes, for the same source commit — so the artifact validated in
 * INTEG could never be the artifact promoted to PROD. Build-once promotion was not unimplemented,
 * it was impossible.
 *
 * Now the values are resolved at RUN time from `/config.json`, which is deployed ALONGSIDE the
 * immutable bundle and differs per environment. Same `assets/index-*.js` everywhere; only the
 * small JSON file beside it changes.
 *
 * ── WHAT MAY LIVE HERE ───────────────────────────────────────────────────────────────────────────
 * PUBLIC CLIENT IDENTIFIERS ONLY. An API base URL, a tenant id, a SPA client id, an OAuth scope and
 * an environment label are all public by construction — the browser sends them to Microsoft on every
 * login and anyone can read them out of the shipped bundle. A SECRET MUST NEVER BE ADDED TO THIS
 * OBJECT OR TO config.json: everything here is downloadable by anyone who can load the page.
 *
 * ── ORDERING CONTRACT ────────────────────────────────────────────────────────────────────────────
 * `loadRuntimeConfig()` MUST complete before any module that reads `config` is imported.
 * `src/main.tsx` enforces this by awaiting the load and only then dynamically importing the app.
 * This is not a stylistic preference: `src/auth/msalConfig.ts` constructs `new
 * PublicClientApplication(...)` at MODULE scope, so it reads `config.entraClientId` the instant it
 * is imported. Import it too early and MSAL is built against an empty client id.
 */

/** The shape of the SPA's public configuration. Public identifiers only — never a secret. */
export type AppConfig = {
  /** Base URL of the target API, without a trailing slash. */
  apiBaseUrl: string;
  /** Microsoft Entra SPA application (client) id. */
  entraClientId: string;
  /** Microsoft Entra directory (tenant) id. */
  entraTenantId: string;
  /** OAuth scope requested for the Sofexia HR API. */
  entraApiScope: string;
  /** Banner label naming this environment. Supplied per environment by config.json. */
  envLabel: string;
  /**
   * Whether to render the non-production warning strip (go-live hotfix §4).
   *
   * OPT-IN, and default FALSE. The banner used to key on `envLabel`, which is REQUIRED — so every
   * deployed environment carried it, PROD included, and INTEG carried a snapshot date it no longer
   * served. An environment now declares that it is abnormal; it no longer says so merely by
   * existing. Kept as a capability because a temporary warning is a real need.
   */
  showEnvBanner: boolean;
  /** When true: bypass MSAL and read a synthetic bearer token from localStorage. Playwright only. */
  testAuth: boolean;
  /**
   * When true: show the local email/password auth UI. The API remains the authority on whether
   * local auth is actually enabled (LOCAL_AUTH_JWT_SECRET); this only hides the form in a target
   * where the backend secret is not configured.
   */
  localAuth: boolean;
};

/**
 * Build-time environment, read defensively.
 *
 * `import.meta.env` is injected by Vite. Under plain Node — which is how the runtime-config tests
 * import this module directly, following the same pattern as `e2e/msal-logout.spec.ts` — it does
 * not exist, and `import.meta.env.VITE_X` would throw a TypeError on module load. Tolerating its
 * absence is what makes the validation logic below unit-testable without a browser or a server.
 */
const buildEnv: Record<string, string | undefined> =
  (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};

/**
 * A release build bakes NO environment values (see apps/web/.env.release), so `buildEnv` yields
 * empty strings and `/config.json` is the only possible source. A local dev or Playwright build
 * keeps reading `.env.development`, so nothing about local work changes.
 */
const RUNTIME_CONFIG_REQUIRED = buildEnv.VITE_RUNTIME_CONFIG === 'required';

/**
 * The live configuration object every consumer imports.
 *
 * It is deliberately MUTABLE and deliberately NOT `as const`. Consumers keep the exact same
 * `config.apiBaseUrl` access they always had; the only difference is that the values arrive from
 * `loadRuntimeConfig()` before any of them run.
 */
export const config: AppConfig = {
  apiBaseUrl: normalizeBaseUrl(buildEnv.VITE_API_BASE_URL ?? ''),
  entraClientId: buildEnv.VITE_ENTRA_CLIENT_ID ?? '',
  entraTenantId: buildEnv.VITE_ENTRA_TENANT_ID ?? '',
  entraApiScope: buildEnv.VITE_ENTRA_API_SCOPE ?? '',
  envLabel: buildEnv.VITE_ENV_LABEL ?? 'UNKNOWN',
  showEnvBanner: buildEnv.VITE_SHOW_ENV_BANNER === 'true',
  testAuth: buildEnv.VITE_TEST_AUTH === 'true',
  localAuth: (buildEnv.VITE_LOCAL_AUTH ?? 'true') !== 'false',
};

/** localStorage key Playwright injects the synthetic token into (test-auth mode only). */
export const TEST_TOKEN_KEY = 'test_access_token';

/** localStorage key holding the local (Sofexia-issued) auth session (access + refresh tokens). */
export const LOCAL_SESSION_KEY = 'sofexiahr-local-session';

/** Where the per-environment runtime configuration is served from, relative to the site root. */
export const RUNTIME_CONFIG_PATH = '/config.json';

function normalizeBaseUrl(value: string): string {
  return value.replace(/\/+$/, '');
}

/** The fields a deployed build cannot function without. */
const REQUIRED_FIELDS = [
  'apiBaseUrl',
  'entraClientId',
  'entraTenantId',
  'entraApiScope',
  'envLabel',
] as const;

export class RuntimeConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RuntimeConfigError';
  }
}

/**
 * Merge a parsed `/config.json` document into `config`.
 *
 * Exported separately from the fetch so it can be unit-tested without a network, and so the
 * validation rules are one readable list rather than something buried in a promise chain.
 *
 * Unknown keys are IGNORED rather than copied across. config.json is fetched from the network and
 * assigning it wholesale would let whatever is served decide the shape of the app's configuration.
 */
export function applyRuntimeConfig(raw: unknown): void {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new RuntimeConfigError(`${RUNTIME_CONFIG_PATH} is not a JSON object.`);
  }
  const doc = raw as Record<string, unknown>;

  const str = (key: string): string | undefined => {
    const v = doc[key];
    if (v === undefined || v === null) return undefined;
    if (typeof v !== 'string') {
      throw new RuntimeConfigError(`${RUNTIME_CONFIG_PATH}: "${key}" must be a string.`);
    }
    return v;
  };
  const bool = (key: string): boolean | undefined => {
    const v = doc[key];
    if (v === undefined || v === null) return undefined;
    if (typeof v !== 'boolean') {
      throw new RuntimeConfigError(`${RUNTIME_CONFIG_PATH}: "${key}" must be true or false.`);
    }
    return v;
  };

  const apiBaseUrl = str('apiBaseUrl');
  if (apiBaseUrl !== undefined) config.apiBaseUrl = normalizeBaseUrl(apiBaseUrl);

  const entraClientId = str('entraClientId');
  if (entraClientId !== undefined) config.entraClientId = entraClientId;

  const entraTenantId = str('entraTenantId');
  if (entraTenantId !== undefined) config.entraTenantId = entraTenantId;

  const entraApiScope = str('entraApiScope');
  if (entraApiScope !== undefined) config.entraApiScope = entraApiScope;

  const envLabel = str('envLabel');
  if (envLabel !== undefined) config.envLabel = envLabel;

  const localAuth = bool('localAuth');
  if (localAuth !== undefined) config.localAuth = localAuth;

  // Opt-in warning strip. Absent means OFF, which is what makes an ordinary environment silent
  // without anybody having to remember to say so.
  const showEnvBanner = bool('showEnvBanner');
  if (showEnvBanner !== undefined) config.showEnvBanner = showEnvBanner;

  // `testAuth` is DELIBERATELY not readable from config.json. It bypasses MSAL entirely and trusts
  // a bearer token out of localStorage, so it is a build-time Playwright affordance and must never
  // become something a deployed environment can switch on by editing a file next to the bundle.
}

/** Throw unless every field a deployed build needs is present and non-empty. */
export function assertConfigUsable(): void {
  const missing = REQUIRED_FIELDS.filter((f) => !config[f]);
  if (missing.length > 0) {
    throw new RuntimeConfigError(
      `${RUNTIME_CONFIG_PATH} did not supply: ${missing.join(', ')}. ` +
        'The application cannot start without them.',
    );
  }
}

/**
 * Fetch and apply `/config.json`.
 *
 * ⚠ THE TRAP THIS GUARDS AGAINST. A dev server — and any SPA host with a navigation fallback —
 * answers an unknown path with `index.html` and a cheerful **200**. Parsing that as JSON would
 * either throw something unhelpful or, worse, succeed against a document that is not configuration.
 * So the response is accepted ONLY when it is 200 AND its content type is JSON. Sofexia HR has hit
 * exactly this failure before (SHR-P46: a relative `/api/...` fetch in an E2E page reached the Vite
 * dev server, which returned index.html with 200, and a security check reported a route as ALLOWED
 * that did not exist).
 *
 * Absence is not an error in a LOCAL build: `.env.development` already supplied the values, and
 * `config.json` is simply not there. Absence IS a fatal error in a release build, because a release
 * build baked nothing and would otherwise boot pointing its API calls at the empty string.
 */
export async function loadRuntimeConfig(
  opts: { fetchImpl?: typeof fetch; required?: boolean } = {},
): Promise<void> {
  const fetchImpl = opts.fetchImpl ?? globalThis.fetch;
  // `required` is injectable so the release-build behaviour can be tested directly. It defaults to
  // what this build actually is, so no caller has to know about it.
  const required = opts.required ?? RUNTIME_CONFIG_REQUIRED;
  let doc: unknown = null;

  try {
    const res = await fetchImpl(RUNTIME_CONFIG_PATH, {
      cache: 'no-store',
      headers: { accept: 'application/json' },
    });
    if (res.ok) {
      const contentType = res.headers.get('content-type') ?? '';
      if (contentType.toLowerCase().includes('json')) {
        doc = await res.json();
      } else if (required) {
        throw new RuntimeConfigError(
          `${RUNTIME_CONFIG_PATH} was served as "${contentType || 'an unknown type'}" rather than ` +
            'JSON. A navigation fallback is almost certainly answering with index.html.',
        );
      }
    } else if (required) {
      throw new RuntimeConfigError(
        `${RUNTIME_CONFIG_PATH} could not be loaded (HTTP ${res.status}).`,
      );
    }
  } catch (err) {
    if (required) throw err;
    // LOCAL/dev build: no config.json is the normal, expected case.
    doc = null;
  }

  if (doc !== null) applyRuntimeConfig(doc);
  if (required) assertConfigUsable();
}

/** True when this build refuses to start without `/config.json`. Exported for diagnostics/tests. */
export const runtimeConfigRequired = RUNTIME_CONFIG_REQUIRED;
